<script setup>
  /**
   * Responsibilities:
   * - Ensure required authentication state.
   * - Middleware ensures application config has been properly routed, hydrated, and transitions set
   * - Retrieve and pass proper prop values to appModule
   */
  import AppModule from '~/components/app-config/app-modules/AppModule.vue'
  import { useApplicationConfigStore } from '~/store/application-config'

  definePageMeta({
    middleware: [
      'anonymous-user-experiments',
      'app-config-page',
    ],
    pageTransition: {
      mode: 'out-in'
    }
  })

  const applicationConfigStore = useApplicationConfigStore()
  const { getModuleFromSlug } = applicationConfigStore
  const { applicationConfig, submitSections } = storeToRefs(applicationConfigStore)
  const { slug } = getAppConfigRouteData()
  const applicationModule = getModuleFromSlug(slug)
  if (!applicationModule || !applicationModule.includesData) {
    throw createError({
      statusCode: 404,
      statusMessage: `Application Module not loaded. Url: ${slug}`,
      fatal: true
    })
  }
</script>

<template>
  <div>
    <AppConfigNavActiveLabelDisplay
      :data-adjust-for-rep-image="applicationModule?.config?.showRepImage"
      :data-cy="`breadcrumb-${slug}`"
    />
    <AppModule
      :data-show-rep-image="applicationModule?.config?.showRepImage"
      v-if="applicationModule"
      :showConfigSkip="applicationConfig?.showSkip"
      :applicationModule="applicationModule"
      :submitSections="applicationModule?.moduleSections || applicationConfig?.submitSections || submitSections"
      :moduleUrlPrefix="applicationConfig?.moduleUrlPrefix"
      :configPrevious="applicationConfig?.urlPrevious"
      :configNext="applicationConfig?.urlNext"
      :configSkip="applicationConfig?.urlSkip"
    />
  </div>
</template>

<!-- css -->
<style lang="scss" scoped>
  $mobile-extra-rep-space: 40px;
  $desktop-extra-rep-space: 64px;
  [data-adjust-for-rep-image="true"] {
    padding-left: 0px;
    padding-left: $mobile-extra-rep-space;
    @include media-breakpoint-up(sm) {
      padding-left: $desktop-extra-rep-space;
    }
  }
  [data-show-rep-image="true"] {
    position: relative;
    padding-left: $mobile-extra-rep-space;
    @include media-breakpoint-up(sm) {
      padding-left: $desktop-extra-rep-space;
    }
  }
  [data-show-rep-image="true"]::before {
    content: '';
    // if we want to dynamically target an actual rep's image
    // we might need to host the images in s3, and pass the image's url down
    // as dynamically handling this might be tricky with a nuxt hosted image.
    background-image: url('/public/images/explainer.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    aspect-ratio: 1;
    position: absolute;
    border-radius: 100%;
    width: 32px;
    left: 0;
    border: 1px solid $neutral-300;
    z-index: 1; // Ensure the image is above other content as it animates
    @include media-breakpoint-up(sm) {
      width: 48px;
      left: 0;
      top: -6px;
    }
  }
</style>
